import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';

import { HEADER_ID, PageLayout } from '@/components/templates/PageLayout';
import {
  CmBaseTable,
  CmBox,
  CmCard,
  CmFlex,
  CmHeadingPagination,
  CmLink,
  CmLongLoading,
  CmMessageCard,
  CmScrollBox,
  CmTableBodyRow,
  CmTd,
  CmTooltip,
  ReactSingleSelectInput,
  SelectForm,
  useCounter,
} from '@/components/uiParts';
import { LIMIT_PER_PAGE } from '@/domain/models/Pagination';
import { DateDomainService } from '@/domain/services';
import { useTargetsDomHeight } from '@/hooks/Dom';
import { redirect404Page } from '@/lib/AccessControl';
import { LocalStorageHandler } from '@/lib/LocalStorageHandler';
import { nullToEmptyString } from '@/lib/util';
import { ommisionText } from '@/lib/util';
import { MenuManagementBrandMenuHour } from '@/pages/menu-managements/business-hours/service';
import {
  businessHourTableThs,
  menuHourPeriodTimes,
} from '@/pages/menu-managements/business-hours/service';
import { HopeMenuManagementJobNotice } from '@/pages/menu-managements/components/Alert/HopeMenuManagementJobNotice';
import {
  MenuManagementSearchArea,
  SEARCH_AREA_ID,
} from '@/pages/menu-managements/components/SearchAndDrag';
import { routes } from '@/router';
import { useAssociateBrandList } from '@/services/AssociateBrand/hooks/list/useAssociateBrandList';
import { useManagerAccountMe } from '@/services/AuthEnterprise/hooks';
import { useMenuManagementBrandMenuHourList } from '@/services/BrandMenuHour/hooks/list/useMenuManagementBrandMenuHourList';

const MenuManagementBusinessHoursPage: React.FC = (): JSX.Element => {
  const history = useHistory();
  const { managerAccountMe } = useManagerAccountMe();
  /* アクセス制御 */
  redirect404Page('menu_management', history, managerAccountMe);
  const [brandMenuHours, setBrandMenuHours] = useState<
    MenuManagementBrandMenuHour[]
  >([]);
  const [searchCount, setSearchCount] = useState(0);
  const [associateBrandSelectOptions, setAssociateBrandSelectOptions] =
    useState<SelectForm[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [timer, setTimer] = useState<number>();

  const [targetAssociateBrandSelect, setTargetAssociateBrandSelect] =
    useState<SelectForm | null>({
      label: '',
      value: '',
    });

  const { count, decrement, increment, customizeCount } = useCounter(1);
  const [titleJa, setTitleJa] = useState<string>('');

  const getListQuery = {
    associateBrandId: targetAssociateBrandSelect?.value ?? '',
    currentPage: 1,
    limit: LIMIT_PER_PAGE,
    title: titleJa,
  } as const;

  const handleInputTitleJa = (e: React.ChangeEvent<HTMLInputElement>) => {
    const title = e.target.value;
    setTitleJa(title);

    clearTimeout(Number(timer));
    const newTimer = setTimeout(async () => {
      setIsLoading(true);
      const { brand_menu_hours, count } =
        await useMenuManagementBrandMenuHourList({
          ...getListQuery,
          currentPage: 1,
          title,
        });
      customizeCount(1);
      setSearchCount(count);
      setBrandMenuHours(brand_menu_hours);
      setIsLoading(false);
    }, 700);
    setTimer(Number(newTimer));
  };

  const associateBrandSelectInput: ReactSingleSelectInput = {
    value: targetAssociateBrandSelect,
    options: associateBrandSelectOptions,
    onChange: async (associateBrand: SelectForm | null) => {
      if (
        associateBrand === null ||
        associateBrand.value === targetAssociateBrandSelect?.value
      )
        return;
      const associateBrandId = associateBrand.value;
      setIsLoading(true);
      setTargetAssociateBrandSelect(associateBrand);
      const { brand_menu_hours, count } =
        await useMenuManagementBrandMenuHourList({
          ...getListQuery,
          currentPage: 1,
          associateBrandId,
        });
      customizeCount(1);
      setSearchCount(count);
      setBrandMenuHours(brand_menu_hours);
      setIsLoading(false);
    },
    onReset: () => {
      setTargetAssociateBrandSelect({
        value: '',
        label: '',
      });
    },
  };

  const toFirstPage = async () => {
    setIsLoading(true);
    const { brand_menu_hours } = await useMenuManagementBrandMenuHourList({
      ...getListQuery,
    });
    setBrandMenuHours(brand_menu_hours);
    customizeCount(1);
    setIsLoading(false);
  };

  const toPreviousPage = async () => {
    setIsLoading(true);
    const previousPage = count - 1;
    const { brand_menu_hours } = await useMenuManagementBrandMenuHourList({
      ...getListQuery,
      currentPage: previousPage,
    });
    setBrandMenuHours(brand_menu_hours);
    decrement();
    setIsLoading(false);
  };

  const toNextPage = async () => {
    setIsLoading(true);
    const nextPage = count + 1;
    const { brand_menu_hours } = await useMenuManagementBrandMenuHourList({
      ...getListQuery,
      currentPage: nextPage,
    });
    setBrandMenuHours(brand_menu_hours);
    increment();
    setIsLoading(false);
  };

  const toLastPage = async () => {
    setIsLoading(true);
    const lastPage = Math.ceil(searchCount / LIMIT_PER_PAGE);
    const { brand_menu_hours } = await useMenuManagementBrandMenuHourList({
      ...getListQuery,
      currentPage: lastPage,
    });
    setBrandMenuHours(brand_menu_hours);
    customizeCount(lastPage);
    setIsLoading(false);
  };

  const { targetsHeight } = useTargetsDomHeight([HEADER_ID, SEARCH_AREA_ID]);

  useEffect(() => {
    (async () => {
      setIsLoading(true);
      const associateBrands = await useAssociateBrandList();
      const associateBrandOptions = associateBrands.map((brand) => {
        return {
          label: brand.name,
          value: brand.id.toString(),
        };
      });
      const { associateBrand } = LocalStorageHandler.get('menuManagement');
      const initialAssociateBrand =
        associateBrand !== undefined
          ? associateBrand
          : associateBrandOptions[0];
      LocalStorageHandler.set('menuManagement', {
        associateBrand: initialAssociateBrand,
      });

      const { brand_menu_hours, count } =
        await useMenuManagementBrandMenuHourList({
          ...getListQuery,
          associateBrandId: initialAssociateBrand.value,
          currentPage: 1,
        });
      setAssociateBrandSelectOptions(associateBrandOptions);
      setTargetAssociateBrandSelect(initialAssociateBrand);
      customizeCount(1);
      setSearchCount(count);
      setBrandMenuHours(brand_menu_hours);
      setIsLoading(false);
    })();
  }, []);

  return (
    <PageLayout
      title={routes.menuManagementBusinessHours.title}
      alert={<HopeMenuManagementJobNotice />}
      {...{ associateBrandSelectInput }}
    >
      <CmBox>
        <MenuManagementSearchArea
          targetInputLabel="メニュー提供時間"
          newPagePath={'/menu-managements/business-hours/new'}
          itemValue={titleJa}
          {...{
            associateBrandSelectInput,
            searchCount,
            handleInputTitleJa,
          }}
        />
        {brandMenuHours.length > 0 ? (
          <CmCard
            padding="20px"
            height={`calc(100vh - ${targetsHeight}px - 20px)`}
          >
            <CmFlex flexDirection="row-reverse" my="5px">
              <CmHeadingPagination
                totalNum={searchCount}
                currentPage={count}
                LimitPerPage={LIMIT_PER_PAGE}
                {...{ toFirstPage, toNextPage, toPreviousPage, toLastPage }}
              />
            </CmFlex>
            <CmScrollBox>
              <CmBaseTable ths={businessHourTableThs}>
                {brandMenuHours.map((brandMenuHour, index) => {
                  const { title, memo } = brandMenuHour;
                  const nonNullableMemo = nullToEmptyString(memo);
                  const { datetimeFormat } = DateDomainService;
                  const titleJa = title.translation.ja;
                  const menuHourPeriodTimeText = menuHourPeriodTimes(
                    brandMenuHour.day_of_week_periods
                  );
                  return (
                    <CmTableBodyRow key={index + 1}>
                      <CmTd>
                        <CmLink
                          color="blue.500"
                          to={`/menu-managements/business-hours/${brandMenuHour.id}/edit`}
                        >
                          <CmTooltip label={titleJa}>
                            {ommisionText(titleJa, 20)}
                          </CmTooltip>
                        </CmLink>
                      </CmTd>
                      <CmTd>
                        <CmTooltip label={menuHourPeriodTimeText}>
                          {ommisionText(menuHourPeriodTimeText, 20)}
                        </CmTooltip>
                      </CmTd>
                      <CmTd>
                        <CmTooltip label={memo}>
                          {ommisionText(nonNullableMemo, 20)}
                        </CmTooltip>
                      </CmTd>
                      <CmTd textAlign="center">
                        <CmTooltip label={brandMenuHour.updated_at}>
                          {datetimeFormat(new Date(brandMenuHour.updated_at))}
                        </CmTooltip>
                      </CmTd>
                    </CmTableBodyRow>
                  );
                })}
              </CmBaseTable>
            </CmScrollBox>
          </CmCard>
        ) : (
          <>
            {!isLoading && (
              <CmMessageCard margin="100px auto 0">
                検索結果に一致するメニュー提供時間が見当たりませんでした。
              </CmMessageCard>
            )}
          </>
        )}
        {isLoading && <CmLongLoading />}
      </CmBox>
    </PageLayout>
  );
};

export default MenuManagementBusinessHoursPage;
